import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// ─── Types ────────────────────────────────────────────────────────────────────

export type Theme = 'light' | 'dark' | 'system'

interface UIState {
  // Theme
  theme: Theme
  resolvedTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void

  // Sidebar
  sidebarCollapsed: boolean
  sidebarMobileOpen: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleMobileSidebar: () => void
  closeMobileSidebar: () => void

  // Navigation
  activeModule: string
  setActiveModule: (module: string) => void

  // Command palette
  commandOpen: boolean
  setCommandOpen: (open: boolean) => void
}

// ─── Theme resolution helper ──────────────────────────────────────────────────

function getResolvedTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    if (typeof window === 'undefined') return 'dark'
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }
  return theme
}

function applyTheme(resolved: 'light' | 'dark') {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark')
  } else {
    root.classList.remove('dark')
  }
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Theme
      theme: 'dark',
      resolvedTheme: 'dark',

      setTheme: (theme) => {
        const resolved = getResolvedTheme(theme)
        applyTheme(resolved)
        set({ theme, resolvedTheme: resolved })
      },

      // Sidebar
      sidebarCollapsed: false,
      sidebarMobileOpen: false,

      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      toggleMobileSidebar: () =>
        set((s) => ({ sidebarMobileOpen: !s.sidebarMobileOpen })),

      closeMobileSidebar: () =>
        set({ sidebarMobileOpen: false }),

      // Navigation
      activeModule: 'dashboard',
      setActiveModule: (module) => {
        set({ activeModule: module })
        get().closeMobileSidebar()
      },

      // Command palette
      commandOpen: false,
      setCommandOpen: (open) => set({ commandOpen: open }),
    }),
    {
      name: 'crm-ui',
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
      onRehydrateStorage: () => (state) => {
        // Tras leer localStorage, aplicar clase .dark y resolvedTheme
        if (!state) return
        const resolved = getResolvedTheme(state.theme)
        applyTheme(resolved)
        state.resolvedTheme = resolved
      },
    }
  )
)

// ─── Theme initializer (call once at app boot, antes del render) ───────────────

export function initTheme() {
  const stored = localStorage.getItem('crm-ui')
  let theme: Theme = 'dark'

  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      theme = parsed?.state?.theme ?? 'dark'
    } catch {
      // ignore
    }
  }

  const resolved = getResolvedTheme(theme)
  applyTheme(resolved)

  // Escuchar cambio de preferencia del SO si theme === system
  if (typeof window !== 'undefined' && theme === 'system') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const current = useUIStore.getState().theme
      if (current === 'system') {
        const r = getResolvedTheme('system')
        applyTheme(r)
        useUIStore.setState({ resolvedTheme: r })
      }
    }
    mq.addEventListener?.('change', onChange)
  }
}
